import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { ClipboardList, Plus, X, BookOpen, Award, Users, Pencil, Trash2, Eye, CheckCircle2, Clock, AlertCircle, Download } from 'lucide-react';
import axios from 'axios';
import { printReportCard } from '../../lib/printReportCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXAM_TYPES = [
  { value: 'unit_test', label: 'Unit Test' },
  { value: 'midterm', label: 'Mid Term' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'half_yearly', label: 'Half Yearly' },
  { value: 'final', label: 'Final / Annual' },
];

const STATUS_COLORS = {
  upcoming: 'bg-blue-100 text-blue-700',
  ongoing: 'bg-amber-100 text-amber-700',
  completed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-slate-100 text-slate-500',
};

const DEFAULT_SUBJECTS = ['English', 'Hindi', 'Mathematics', 'Science', 'Social Studies', 'Computer Science', 'Physical Education', 'Art'];

export default function ExamManagement() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [tab, setTab] = useState('exams');
  const [exams, setExams] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Forms
  const [showExamForm, setShowExamForm] = useState(false);
  const [showSubjectForm, setShowSubjectForm] = useState(false);
  const [showMarksForm, setShowMarksForm] = useState(false);
  const [showReportCard, setShowReportCard] = useState(false);

  const [examForm, setExamForm] = useState({ name: '', exam_type: 'unit_test', class_ids: [], start_date: '', end_date: '' });
  const [subjectForm, setSubjectForm] = useState({ name: '', code: '', class_id: '', max_marks: 100 });
  const [marksState, setMarksState] = useState({ exam_id: '', subject_id: '', class_id: '', entries: [], students: [] });
  const [reportData, setReportData] = useState(null);
  const [reportStudentId, setReportStudentId] = useState('');
  const [reportClassFilter, setReportClassFilter] = useState('');
  const [allStudents, setAllStudents] = useState([]);
  const [selectedClassForSubjects, setSelectedClassForSubjects] = useState('');

  const fetchAll = async () => {
    try {
      const [examsRes, subjectsRes, classesRes, statsRes] = await Promise.all([
        axios.get(`${API}/exams`, { headers }),
        axios.get(`${API}/exams/subjects`, { headers }),
        axios.get(`${API}/classes`, { headers }),
        axios.get(`${API}/exams/stats/overview`, { headers }),
      ]);
      setExams(examsRes.data.data.exams || []);
      setSubjects(subjectsRes.data.data || []);
      setClasses(classesRes.data.data || []);
      setStats(statsRes.data.data);
    } catch (err) {
      console.error('Fetch error:', err);
    }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  // ── Exam CRUD ──
  const handleCreateExam = async (e) => {
    e.preventDefault();
    if (!examForm.name.trim()) return toast.error('Exam name is required');
    try {
      await axios.post(`${API}/exams`, examForm, { headers });
      toast.success('Exam created successfully');
      setShowExamForm(false);
      setExamForm({ name: '', exam_type: 'unit_test', class_ids: [], start_date: '', end_date: '' });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create exam'); }
  };

  const handleDeleteExam = async (examId) => {
    if (!window.confirm('Delete this exam and all associated marks?')) return;
    try {
      await axios.delete(`${API}/exams/${examId}`, { headers });
      toast.success('Exam deleted');
      fetchAll();
    } catch (err) { toast.error('Failed to delete'); }
  };

  const handleExamStatus = async (examId, status) => {
    try {
      await axios.put(`${API}/exams/${examId}/status?status=${status}`, {}, { headers });
      toast.success(`Status updated to ${status}`);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  // ── Subject CRUD ──
  const handleCreateSubject = async (e) => {
    e.preventDefault();
    if (!subjectForm.name.trim() || !subjectForm.class_id) return toast.error('Name and class required');
    try {
      await axios.post(`${API}/exams/subjects`, subjectForm, { headers });
      toast.success('Subject added');
      setShowSubjectForm(false);
      setSubjectForm({ name: '', code: '', class_id: '', max_marks: 100 });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteSubject = async (subjectId) => {
    if (!window.confirm('Delete this subject?')) return;
    try {
      await axios.delete(`${API}/exams/subjects/${subjectId}`, { headers });
      toast.success('Subject deleted');
      fetchAll();
    } catch (err) { toast.error('Failed to delete'); }
  };

  // ── Marks Entry ──
  const openMarksEntry = async (examId) => {
    setMarksState({ ...marksState, exam_id: examId, subject_id: '', class_id: '', entries: [], students: [] });
    setShowMarksForm(true);
  };

  const loadStudentsForMarks = async () => {
    if (!marksState.class_id || !marksState.subject_id) return;
    try {
      const studentsRes = await axios.get(`${API}/students?class_id=${marksState.class_id}&limit=200`, { headers });
      const studentsList = studentsRes.data.data.students || [];

      // Load existing marks
      const marksRes = await axios.get(`${API}/exams/marks?exam_id=${marksState.exam_id}&subject_id=${marksState.subject_id}&class_id=${marksState.class_id}`, { headers });
      const existingMarks = marksRes.data.data || [];
      const marksMap = {};
      existingMarks.forEach(m => { marksMap[m.student_id] = m.marks_obtained; });

      const entries = studentsList.map(s => ({
        student_id: s.student_id,
        student_name: s.full_name,
        admission_number: s.admission_number,
        marks_obtained: marksMap[s.student_id] ?? '',
        remarks: '',
      }));

      setMarksState(prev => ({ ...prev, entries, students: studentsList }));
    } catch (err) {
      toast.error('Failed to load students');
    }
  };

  useEffect(() => {
    if (showMarksForm && marksState.class_id && marksState.subject_id) {
      loadStudentsForMarks();
    }
  }, [marksState.class_id, marksState.subject_id]);

  const handleSaveMarks = async () => {
    const valid = marksState.entries.filter(e => e.marks_obtained !== '' && e.marks_obtained !== undefined);
    if (valid.length === 0) return toast.error('Enter at least one mark');
    try {
      await axios.post(`${API}/exams/marks`, {
        exam_id: marksState.exam_id,
        subject_id: marksState.subject_id,
        entries: valid.map(e => ({ student_id: e.student_id, marks_obtained: parseFloat(e.marks_obtained) || 0, remarks: e.remarks })),
      }, { headers });
      toast.success(`${valid.length} marks saved`);
      setShowMarksForm(false);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save marks'); }
  };

  // ── Report Card ──
  const loadReportCard = async (studentId) => {
    try {
      const res = await axios.get(`${API}/exams/report-card/${studentId}`, { headers });
      setReportData(res.data.data);
      setShowReportCard(true);
    } catch (err) { toast.error('Failed to load report card'); }
  };

  const loadStudentsForReport = async (classId) => {
    setReportClassFilter(classId);
    setReportStudentId('');
    if (!classId) { setAllStudents([]); return; }
    try {
      const res = await axios.get(`${API}/students?class_id=${classId}&limit=200`, { headers });
      setAllStudents(res.data.data.students || []);
    } catch { setAllStudents([]); }
  };

  const filteredSubjects = selectedClassForSubjects ? subjects.filter(s => s.class_id === selectedClassForSubjects) : subjects;

  const toggleClassForExam = (classId) => {
    setExamForm(prev => ({
      ...prev,
      class_ids: prev.class_ids.includes(classId) ? prev.class_ids.filter(id => id !== classId) : [...prev.class_ids, classId],
    }));
  };

  if (loading) return <div className="flex items-center justify-center py-20 text-slate-400" data-testid="exam-loading">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="exam-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="exam-management-title">Exam Management</h1>
          <p className="text-sm text-slate-500 mt-1">Create exams, manage subjects, enter marks, generate report cards</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowSubjectForm(true)} data-testid="add-subject-btn"><BookOpen className="h-4 w-4 mr-2" /> Add Subject</Button>
          <Button onClick={() => { setShowExamForm(true); setExamForm({ name: '', exam_type: 'unit_test', class_ids: [], start_date: '', end_date: '' }); }} className="bg-teal-500 hover:bg-teal-600 text-white" data-testid="create-exam-btn"><Plus className="h-4 w-4 mr-2" /> Create Exam</Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-xs text-slate-400">Total Exams</p><p className="text-2xl font-bold text-slate-900">{stats.total_exams}</p></div>
          <div className="bg-white rounded-xl border border-blue-100 p-4"><p className="text-xs text-blue-400">Upcoming</p><p className="text-2xl font-bold text-blue-600">{stats.upcoming}</p></div>
          <div className="bg-white rounded-xl border border-emerald-100 p-4"><p className="text-xs text-emerald-400">Completed</p><p className="text-2xl font-bold text-emerald-600">{stats.completed}</p></div>
          <div className="bg-white rounded-xl border border-slate-100 p-4"><p className="text-xs text-slate-400">Total Subjects</p><p className="text-2xl font-bold text-slate-900">{stats.total_subjects}</p></div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
        {['exams', 'subjects', 'report-cards'].map(t => (
          <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 text-sm font-medium rounded-md capitalize ${tab === t ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'}`} data-testid={`tab-${t}`}>
            {t.replace('-', ' ')}
          </button>
        ))}
      </div>

      {/* ── Exams Tab ── */}
      {tab === 'exams' && (
        <div className="space-y-3 mt-6">
          {exams.length === 0 ? (
            <div className="text-center py-16 bg-slate-50 rounded-2xl border border-dashed border-slate-200" data-testid="no-exams">
              <ClipboardList className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No exams created yet</p>
              <p className="text-sm text-slate-400 mt-1">Create your first exam to get started</p>
            </div>
          ) : exams.map(exam => (
            <div key={exam.exam_id} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`exam-${exam.exam_id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-3">
                    <h3 className="font-semibold text-slate-900">{exam.name}</h3>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_COLORS[exam.status]}`}>{exam.status}</span>
                    <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full capitalize">{exam.exam_type.replace('_', ' ')}</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    {exam.start_date && `${exam.start_date} → ${exam.end_date}`}
                    {exam.classes && ` | Classes: ${exam.classes.map(c => c.name).join(', ')}`}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {exam.status === 'upcoming' && (
                    <Button variant="outline" size="sm" onClick={() => handleExamStatus(exam.exam_id, 'ongoing')} data-testid={`start-exam-${exam.exam_id}`}>
                      <Clock className="h-3.5 w-3.5 mr-1" /> Start
                    </Button>
                  )}
                  {exam.status === 'ongoing' && (
                    <>
                      <Button variant="outline" size="sm" onClick={() => openMarksEntry(exam.exam_id)} data-testid={`enter-marks-${exam.exam_id}`}>
                        <Pencil className="h-3.5 w-3.5 mr-1" /> Enter Marks
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleExamStatus(exam.exam_id, 'completed')} data-testid={`complete-exam-${exam.exam_id}`}>
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Complete
                      </Button>
                    </>
                  )}
                  {exam.status === 'completed' && (
                    <Button variant="outline" size="sm" onClick={() => openMarksEntry(exam.exam_id)} data-testid={`view-marks-${exam.exam_id}`}>
                      <Eye className="h-3.5 w-3.5 mr-1" /> View Marks
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-600" onClick={() => handleDeleteExam(exam.exam_id)} data-testid={`delete-exam-${exam.exam_id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Subjects Tab ── */}
      {tab === 'subjects' && (
        <div className="space-y-4 mt-6">
          <div className="flex gap-2 items-center">
            <Select value={selectedClassForSubjects || 'all'} onValueChange={v => setSelectedClassForSubjects(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-48 h-9" data-testid="filter-class-subjects"><SelectValue placeholder="All Classes" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Classes</SelectItem>
                {classes.map(c => <SelectItem key={c.class_id} value={c.class_id}>{c.name} {c.section}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {filteredSubjects.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-2xl border border-dashed border-slate-200"><BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-400">No subjects found</p></div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {filteredSubjects.map(s => (
                <div key={s.subject_id} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between" data-testid={`subject-${s.subject_id}`}>
                  <div>
                    <p className="font-medium text-slate-900">{s.name}</p>
                    <p className="text-xs text-slate-400">{s.class_name} {s.section} | Code: {s.code} | Max: {s.max_marks}</p>
                  </div>
                  <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-600" onClick={() => handleDeleteSubject(s.subject_id)} data-testid={`delete-subject-${s.subject_id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Report Cards Tab ── */}
      {tab === 'report-cards' && (
        <div className="space-y-4 mt-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="font-semibold text-slate-900 mb-3">View Student Report Card</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
              <div>
                <Label className="text-xs text-slate-500">Class</Label>
                <Select value={reportClassFilter} onValueChange={loadStudentsForReport}>
                  <SelectTrigger data-testid="report-class-select"><SelectValue placeholder="Select class" /></SelectTrigger>
                  <SelectContent>{classes.map(c => <SelectItem key={c.class_id} value={c.class_id}>{c.name} {c.section}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Student</Label>
                <Select value={reportStudentId} onValueChange={setReportStudentId} disabled={!reportClassFilter || allStudents.length === 0}>
                  <SelectTrigger data-testid="report-student-select"><SelectValue placeholder={allStudents.length === 0 ? 'Select class first' : 'Select student'} /></SelectTrigger>
                  <SelectContent>
                    {allStudents.map(s => (
                      <SelectItem key={s.student_id} value={s.student_id}>{s.full_name} ({s.admission_number})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={() => { if (reportStudentId) loadReportCard(reportStudentId); }} className="bg-teal-500 hover:bg-teal-600 text-white h-10" disabled={!reportStudentId} data-testid="load-report-btn">
                <Award className="h-4 w-4 mr-2" /> View Report
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Create Exam Modal ── */}
      {showExamForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="create-exam-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4"><h2 className="text-lg font-bold">Create Exam</h2><button onClick={() => setShowExamForm(false)}><X className="h-5 w-5 text-slate-400" /></button></div>
            <form onSubmit={handleCreateExam} className="space-y-4">
              <div><Label className="text-xs text-slate-500">Exam Name *</Label><Input value={examForm.name} onChange={e => setExamForm({ ...examForm, name: e.target.value })} placeholder="e.g., Unit Test 1" required data-testid="exam-name-input" /></div>
              <div><Label className="text-xs text-slate-500">Exam Type</Label>
                <Select value={examForm.exam_type} onValueChange={v => setExamForm({ ...examForm, exam_type: v })}>
                  <SelectTrigger data-testid="exam-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{EXAM_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-slate-500">Start Date</Label><Input type="date" value={examForm.start_date} onChange={e => setExamForm({ ...examForm, start_date: e.target.value })} data-testid="exam-start-date" /></div>
                <div><Label className="text-xs text-slate-500">End Date</Label><Input type="date" value={examForm.end_date} onChange={e => setExamForm({ ...examForm, end_date: e.target.value })} data-testid="exam-end-date" /></div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Applicable Classes</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {classes.map(c => (
                    <button type="button" key={c.class_id} onClick={() => toggleClassForExam(c.class_id)}
                      className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${examForm.class_ids.includes(c.class_id) ? 'bg-teal-50 border-teal-200 text-teal-700 font-semibold' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}
                      data-testid={`exam-class-toggle-${c.class_id}`}>
                      {c.name} {c.section}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowExamForm(false)} className="flex-1">Cancel</Button>
                <Button type="submit" className="flex-1 bg-teal-500 hover:bg-teal-600 text-white" data-testid="save-exam-btn">Create Exam</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Create Subject Modal ── */}
      {showSubjectForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="create-subject-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between mb-4"><h2 className="text-lg font-bold">Add Subject</h2><button onClick={() => setShowSubjectForm(false)}><X className="h-5 w-5 text-slate-400" /></button></div>
            <form onSubmit={handleCreateSubject} className="space-y-4">
              <div><Label className="text-xs text-slate-500">Class *</Label>
                <Select value={subjectForm.class_id} onValueChange={v => setSubjectForm({ ...subjectForm, class_id: v })}>
                  <SelectTrigger data-testid="subject-class-select"><SelectValue placeholder="Select class" /></SelectTrigger>
                  <SelectContent>{classes.map(c => <SelectItem key={c.class_id} value={c.class_id}>{c.name} {c.section}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs text-slate-500">Subject Name *</Label>
                <Select value={subjectForm.name} onValueChange={v => setSubjectForm({ ...subjectForm, name: v, code: v.substring(0, 3).toUpperCase() })}>
                  <SelectTrigger data-testid="subject-name-select"><SelectValue placeholder="Choose subject" /></SelectTrigger>
                  <SelectContent>{DEFAULT_SUBJECTS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-slate-500">Subject Code</Label><Input value={subjectForm.code} onChange={e => setSubjectForm({ ...subjectForm, code: e.target.value })} placeholder="e.g., ENG" data-testid="subject-code-input" /></div>
                <div><Label className="text-xs text-slate-500">Max Marks</Label><Input type="number" value={subjectForm.max_marks} onChange={e => setSubjectForm({ ...subjectForm, max_marks: parseInt(e.target.value) || 100 })} data-testid="subject-max-marks" /></div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowSubjectForm(false)} className="flex-1">Cancel</Button>
                <Button type="submit" className="flex-1 bg-teal-500 hover:bg-teal-600 text-white" data-testid="save-subject-btn">Add Subject</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Marks Entry Modal ── */}
      {showMarksForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="marks-entry-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-3xl shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Enter / View Marks</h2>
              <button onClick={() => setShowMarksForm(false)}><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div><Label className="text-xs text-slate-500">Class *</Label>
                <Select value={marksState.class_id} onValueChange={v => setMarksState(prev => ({ ...prev, class_id: v, entries: [], students: [] }))}>
                  <SelectTrigger data-testid="marks-class-select"><SelectValue placeholder="Select class" /></SelectTrigger>
                  <SelectContent>{classes.map(c => <SelectItem key={c.class_id} value={c.class_id}>{c.name} {c.section}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs text-slate-500">Subject *</Label>
                <Select value={marksState.subject_id} onValueChange={v => setMarksState(prev => ({ ...prev, subject_id: v, entries: [], students: [] }))}>
                  <SelectTrigger data-testid="marks-subject-select"><SelectValue placeholder="Select subject" /></SelectTrigger>
                  <SelectContent>
                    {subjects.filter(s => !marksState.class_id || s.class_id === marksState.class_id).map(s => (
                      <SelectItem key={s.subject_id} value={s.subject_id}>{s.name} ({s.max_marks})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {marksState.entries.length > 0 ? (
              <>
                <div className="overflow-x-auto border border-slate-200 rounded-xl">
                  <table className="w-full" data-testid="marks-entry-table">
                    <thead><tr className="bg-slate-50 border-b border-slate-100">
                      <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 w-12">#</th>
                      <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500">Student</th>
                      <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500">Adm No</th>
                      <th className="text-center px-4 py-2 text-xs font-semibold text-slate-500 w-32">Marks</th>
                    </tr></thead>
                    <tbody className="divide-y divide-slate-50">
                      {marksState.entries.map((entry, i) => (
                        <tr key={entry.student_id}>
                          <td className="px-4 py-2 text-sm text-slate-400">{i + 1}</td>
                          <td className="px-4 py-2 text-sm font-medium text-slate-900">{entry.student_name}</td>
                          <td className="px-4 py-2 text-xs text-slate-500 font-mono">{entry.admission_number}</td>
                          <td className="px-4 py-2 text-center">
                            <Input type="number" min="0" className="w-24 h-8 text-center mx-auto"
                              value={entry.marks_obtained}
                              onChange={e => {
                                const newEntries = [...marksState.entries];
                                newEntries[i] = { ...newEntries[i], marks_obtained: e.target.value };
                                setMarksState(prev => ({ ...prev, entries: newEntries }));
                              }}
                              data-testid={`marks-input-${entry.student_id}`} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex gap-2 mt-4 justify-end">
                  <Button variant="outline" onClick={() => setShowMarksForm(false)}>Cancel</Button>
                  <Button onClick={handleSaveMarks} className="bg-teal-500 hover:bg-teal-600 text-white" data-testid="save-marks-btn">Save Marks</Button>
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-slate-400 text-sm">
                {marksState.class_id && marksState.subject_id ? 'Loading students...' : 'Select a class and subject to load students'}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Report Card Modal ── */}
      {showReportCard && reportData && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="report-card-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold">Report Card</h2>
                <p className="text-sm text-slate-500">{reportData.student.name} | {reportData.student.admission_number} | {reportData.student.class_name} {reportData.student.section}</p>
              </div>
              <div className="flex items-center gap-2">
                {reportData.exams.length > 0 && (
                  <button
                    onClick={() => printReportCard(reportData)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-500 hover:bg-teal-600 text-white text-sm font-medium transition-colors"
                  >
                    <Download className="h-4 w-4" /> Download PDF
                  </button>
                )}
                <button onClick={() => setShowReportCard(false)}><X className="h-5 w-5 text-slate-400" /></button>
              </div>
            </div>
            {reportData.exams.length === 0 ? (
              <p className="text-center py-8 text-slate-400">No marks recorded for this student yet.</p>
            ) : reportData.exams.map((exam, idx) => (
              <div key={idx} className="mb-6 last:mb-0">
                <h3 className="font-semibold text-slate-900 mb-2">{exam.exam_name}</h3>
                <div className="overflow-x-auto border border-slate-200 rounded-xl">
                  <table className="w-full">
                    <thead><tr className="bg-slate-50 border-b border-slate-100">
                      <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500">Subject</th>
                      <th className="text-center px-4 py-2 text-xs font-semibold text-slate-500">Max</th>
                      <th className="text-center px-4 py-2 text-xs font-semibold text-slate-500">Obtained</th>
                      <th className="text-center px-4 py-2 text-xs font-semibold text-slate-500">%</th>
                    </tr></thead>
                    <tbody className="divide-y divide-slate-50">
                      {exam.subjects.map((sub, si) => (
                        <tr key={si}>
                          <td className="px-4 py-2 text-sm text-slate-900">{sub.subject}</td>
                          <td className="px-4 py-2 text-sm text-slate-500 text-center">{sub.max_marks}</td>
                          <td className="px-4 py-2 text-sm font-semibold text-center">{sub.obtained}</td>
                          <td className="px-4 py-2 text-sm text-center">
                            <span className={`font-semibold ${sub.percentage >= 75 ? 'text-emerald-600' : sub.percentage >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{sub.percentage}%</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot><tr className="bg-slate-50 border-t border-slate-200">
                      <td className="px-4 py-2 text-sm font-bold text-slate-900">Total</td>
                      <td className="px-4 py-2 text-sm font-bold text-center">{exam.total_marks}</td>
                      <td className="px-4 py-2 text-sm font-bold text-center">{exam.total_obtained}</td>
                      <td className="px-4 py-2 text-sm font-bold text-center">
                        <span className={`${exam.overall_percentage >= 75 ? 'text-emerald-600' : exam.overall_percentage >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{exam.overall_percentage}%</span>
                      </td>
                    </tr></tfoot>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
